import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import tourSteps from './tourSteps';
import './AdminTour.css';

const STORAGE_KEY = 'admin_tour_state';
const SEEN_KEY = 'admin_tour_seen';
const TARGET_POLL_MS = 150;
const TARGET_POLL_MAX_TRIES = 30; // ~4.5s — long enough for theme data to load after navigating

function loadState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed.stepIndex === 'number' ? parsed : null;
  } catch {
    return null;
  }
}

function persistState(state) {
  try {
    if (state) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

// Auto-start is one-time (per browser) so returning admins landing back on the
// Rules page aren't interrupted every visit — the "Take a tour" button always
// remains available to run it again on purpose.
function hasSeenTour() {
  try {
    return localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

function markTourSeen() {
  try {
    localStorage.setItem(SEEN_KEY, '1');
  } catch {
    /* ignore */
  }
}

function computeCardStyle(rect) {
  const margin = 16;
  const cardWidth = 320;
  const estCardHeight = 200;
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;

  let top = rect.bottom + margin;
  if (top + estCardHeight > viewportH) {
    top = Math.max(margin, rect.top - estCardHeight - margin);
  }
  let left = rect.left;
  if (left + cardWidth + margin > viewportW) {
    left = Math.max(margin, viewportW - cardWidth - margin);
  }
  return { top, left };
}

/**
 * Guided walkthrough across every admin page (Home → Rules → Canvas settings).
 * Step position is persisted to sessionStorage, and Next/Back navigate to the
 * right route automatically when a step lives on a different page than the
 * current one — so the tour reads as one continuous walkthrough rather than
 * resetting each time the admin moves to a different settings page.
 */
export default function AdminTour() {
  const location = useLocation();
  const navigate = useNavigate();

  const initial = loadState();
  const [active, setActive] = useState(Boolean(initial?.active));
  const [stepIndex, setStepIndex] = useState(initial?.stepIndex ?? 0);
  const [rect, setRect] = useState(null);

  const step = active ? tourSteps[stepIndex] : null;
  const onCorrectPage = Boolean(step) && location.pathname === step.page;
  // The tour only covers Rules + Canvas settings — no steps live on the theme
  // picker page, so the launcher button has nothing to start there either.
  const tourAvailableOnThisPage = tourSteps.some((s) => s.page === location.pathname);

  useEffect(() => {
    persistState(active ? { active: true, stepIndex } : null);
  }, [active, stepIndex]);

  // Auto-start the very first time an admin lands on the tour's starting page —
  // landing directly on a later page (e.g. a bookmarked Canvas-settings link)
  // does not retroactively pull them back to the start, only arriving at the
  // actual first step's page does. Once shown, it won't auto-start again; the
  // "Take a tour" button still relaunches it any time on purpose.
  useEffect(() => {
    if (active) return;
    if (location.pathname !== tourSteps[0].page) return;
    if (hasSeenTour()) return;
    markTourSeen();
    setStepIndex(0);
    setActive(true);
  }, [active, location.pathname]);

  const goToStep = useCallback(
    (index) => {
      if (index < 0) return;
      if (index >= tourSteps.length) {
        setActive(false);
        return;
      }
      const target = tourSteps[index];
      setStepIndex(index);
      if (target.page !== location.pathname) {
        navigate(target.page);
      }
    },
    [location.pathname, navigate]
  );

  // Locate the current step's target element, re-locating on resize/scroll, and
  // auto-advancing past a step whose element never shows up on this page (e.g. a
  // Live-mode-only step while the current theme is set to Self-Paced) instead of
  // leaving the admin stuck with nothing to point at.
  useEffect(() => {
    setRect(null);
    if (!active || !step || !onCorrectPage) return undefined;

    let tries = 0;
    let cleanupListeners = null;
    let cancelled = false;

    const attach = (el) => {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const update = () => setRect(el.getBoundingClientRect());
      update();
      window.addEventListener('resize', update);
      window.addEventListener('scroll', update, true);
      return () => {
        window.removeEventListener('resize', update);
        window.removeEventListener('scroll', update, true);
      };
    };

    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(`[data-tour="${step.target}"]`);
      if (el) {
        cleanupListeners = attach(el);
        return;
      }
      tries += 1;
      if (tries >= TARGET_POLL_MAX_TRIES) {
        goToStep(stepIndex + 1);
        return;
      }
      timeoutId = window.setTimeout(tick, TARGET_POLL_MS);
    };

    let timeoutId = window.setTimeout(tick, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      if (cleanupListeners) cleanupListeners();
    };
  }, [active, step, onCorrectPage, goToStep]);

  const handleStart = useCallback(() => {
    setActive(true);
    goToStep(0);
  }, [goToStep]);

  const handleNext = useCallback(() => goToStep(stepIndex + 1), [goToStep, stepIndex]);
  const handleBack = useCallback(() => goToStep(stepIndex - 1), [goToStep, stepIndex]);
  const handleSkip = useCallback(() => setActive(false), []);

  return (
    <>
      {tourAvailableOnThisPage ? (
        <button type="button" className="admin-tour-fab" onClick={handleStart}>
          <i className="fa-solid fa-circle-question" aria-hidden /> Take a tour
        </button>
      ) : null}

      {active && step && onCorrectPage ? (
        <div className="admin-tour-overlay">
          {rect ? (
            <div
              className="admin-tour-highlight"
              style={{
                top: rect.top - 6,
                left: rect.left - 6,
                width: rect.width + 12,
                height: rect.height + 12,
              }}
            />
          ) : null}

          <div
            className={`admin-tour-card${rect ? '' : ' admin-tour-card--centered'}`}
            style={rect ? computeCardStyle(rect) : undefined}
          >
            {rect ? (
              <>
                <div className="admin-tour-card-step">
                  Step {stepIndex + 1} of {tourSteps.length}
                </div>
                <h4 className="admin-tour-card-title">{step.title}</h4>
                <p className="admin-tour-card-desc">{step.description}</p>
              </>
            ) : (
              <p className="admin-tour-card-desc">Loading this step…</p>
            )}
            <div className="admin-tour-card-actions">
              <button type="button" className="admin-tour-btn admin-tour-btn--ghost" onClick={handleSkip}>
                Skip
              </button>
              <div className="admin-tour-card-actions-right">
                <button
                  type="button"
                  className="admin-tour-btn"
                  onClick={handleBack}
                  disabled={stepIndex === 0}
                >
                  Back
                </button>
                <button type="button" className="admin-tour-btn admin-tour-btn--primary" onClick={handleNext}>
                  {stepIndex === tourSteps.length - 1 ? 'Finish' : 'Next'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
